import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { getKidSub } from '@/auth/authStore';
import { api } from '@/lib/api';
import { studySourceUrl } from './studySourceUrl';
import type { AcademyQuestion } from '../academy/academyApi';
import type { EquationPracticeChallenge } from './chemicalEquationPractice';
import type { StudyAnalysisReviewItem } from './studyAnalysisFollowUpApi';
import { assertSupportedStudyFile, studyUploadMime } from './studyUploadTypes';

export type StudySubject = {
  id: string;
  subject_key: string;
  display_name: string;
};

export type StudyLink = {
  id: string;
  url: string;
  title: string;
  status: 'active' | 'archived';
  provenance: 'learner_link';
  term: { id: string; label: string } | null;
  subject: { subject_key: string; display_name: string } | null;
  created_at: string;
};

export type StudyTask = {
  id: string;
  title: string;
  source_url: string | null;
  status: 'inbox' | 'today' | 'later' | 'done' | 'archived';
  due_on: string | null;
  term: { id: string; label: string } | null;
  subject: { subject_key: string; display_name: string } | null;
  completed_at: string | null;
  created_at: string;
};

export type StudyTerm = {
  id: string;
  term_number: number;
  label: string;
  starts_on: string | null;
  ends_on: string | null;
  date_provenance: 'official' | 'school' | 'learner' | 'unknown';
  status: 'active' | 'finished' | 'archived';
  is_current: boolean;
  week: number | null;
  subjects?: Array<{ subject_key: string; display_name: string }>;
};

export type StudyHome = {
  setup_status: 'not_started' | 'needs_term' | 'ready';
  school: {
    name: string | null;
    suburb: string | null;
    state: 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA' | null;
    acara_id: string | null;
  };
  academic_year: {
    id: string;
    school_year: number;
    year_level: number | null;
    jurisdiction: string | null;
    timezone: string | null;
  } | null;
  current_term: StudyTerm | null;
  academic_years: Array<{
    id: string;
    school_year: number;
    year_level: number | null;
    status: 'active' | 'archived';
    is_current: boolean;
    terms: StudyTerm[];
  }>;
  terms: StudyTerm[];
  subjects: StudySubject[];
  today: Pick<StudyTask, 'id' | 'title' | 'status' | 'due_on' | 'subject'>[];
  upcoming_assessments: StudyAssessment[];
  review_due_count: number;
};

export type StudyAssessment = {
  id: string;
  title: string;
  assessment_type:
    | 'assignment'
    | 'quiz'
    | 'test'
    | 'exam'
    | 'project'
    | 'oral'
    | 'practical'
    | 'other';
  scope: string | null;
  due_on: string;
  ai_help_policy: 'unknown' | 'allowed' | 'limited' | 'not_allowed';
  status: 'upcoming' | 'completed' | 'archived';
  completed_at: string | null;
  term_id: string;
  term: { term_number: number; label: string } | null;
  subject: { subject_key: string; display_name: string };
  created_at: string;
};

export type CreateStudyAssessmentInput = {
  subject_key: string;
  title: string;
  assessment_type: StudyAssessment['assessment_type'];
  scope?: string | null;
  due_on: string;
  ai_help_policy: StudyAssessment['ai_help_policy'];
};

export type UpdateStudyAssessmentInput = Partial<CreateStudyAssessmentInput> & {
  status?: StudyAssessment['status'];
};

export type StudyDocument = {
  id: string;
  file_name: string;
  /** Learner-chosen name; null uses the analysed topic, else the file name. */
  display_name?: string | null;
  mime_type: string;
  size_bytes: number;
  status: 'uploaded' | 'processing' | 'ready' | 'needs_confirmation' | 'failed';
  classification_state?: 'needs_confirmation' | 'auto_organised' | 'learner_confirmed' | 'undone';
  context_suggestion?: {
    /** `page_analysis` comes from Study page analysis (study-capture-analyse-prd.md §10.1). */
    source: 'file_name' | 'pdf_embedded_text' | 'page_analysis';
    subject_key: string | null;
    topic: string | null;
    year_level?: number | null;
    assessment_context: 'assignment' | 'quiz' | 'test' | 'exam' | 'possible_assessment' | null;
    confidence:
      | { subject: number; topic: number; year: number; assessment: number }
      | 'high'
      | 'medium'
      | 'low';
    auto_organise: boolean;
    analysis_id?: string;
    subject_label?: string;
    material_type?: string;
  } | null;
  term_id: string | null;
  subject: { subject_key: string; display_name: string } | null;
  created_at: string;
  /** List responses only: a short-lived signed preview (page 1 PNG, the photo, or the PDF). */
  thumbnail?: StudyDocumentThumbnail | null;
  /** List responses only: the newest analysis of this material, if any. */
  analysis?: StudyDocumentAnalysisSummary | null;
};

export type StudyDocumentThumbnail = {
  /** `pdf` means the URL is the PDF itself; the app draws page 1. */
  kind: 'image' | 'pdf';
  url: string;
  expires_in: number;
};

export type StudyDocumentAnalysisSummary = {
  status: 'queued' | 'running' | 'partial' | 'succeeded' | 'failed';
  topic: string | null;
  questions_total: number;
  questions_done: number;
};

export type StudyDocumentDetail = StudyDocument & {
  read_url: string;
  read_url_expires_in: number;
  source_revision: string;
};

export type StudyPdfAnnotation = {
  id: string;
  request_id: string;
  source_revision: string;
  page_number: number;
  kind: 'ink' | 'highlight' | 'note';
  geometry: Record<string, unknown>;
  body: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

export type StudyPdfAnnotationInput = {
  request_id: string;
  source_revision: string;
  page_number: number;
} & (
  | {
      kind: 'ink';
      geometry: {
        points: Array<{ x: number; y: number }>;
        colour: 'ink' | 'sky' | 'coral' | 'mint' | 'yellow';
        width: number;
      };
    }
  | {
      kind: 'highlight';
      geometry: {
        x: number;
        y: number;
        width: number;
        height: number;
        colour: 'ink' | 'sky' | 'coral' | 'mint' | 'yellow';
      };
    }
  | { kind: 'note'; geometry: { x: number; y: number }; body: string }
);

export const listStudyPdfAnnotations = (documentId: string, pageNumber: number, cursor?: string) =>
  api<{ items: StudyPdfAnnotation[]; next_cursor: string | null }>(
    `/study/documents/${documentId}/pdf-annotations?page_number=${pageNumber}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );
export const createStudyPdfAnnotation = (documentId: string, input: StudyPdfAnnotationInput) =>
  api<StudyPdfAnnotation>(`/study/documents/${documentId}/pdf-annotations`, {
    method: 'POST',
    body: input,
  });
export const updateStudyPdfAnnotation = (
  documentId: string,
  annotationId: string,
  expectedVersion: number,
  changes: { body?: string; geometry?: Record<string, unknown> },
) =>
  api<StudyPdfAnnotation>(`/study/documents/${documentId}/pdf-annotations/${annotationId}`, {
    method: 'PATCH',
    body: { expected_version: expectedVersion, ...changes },
  });
export const deleteStudyPdfAnnotation = (
  documentId: string,
  annotationId: string,
  expectedVersion: number,
) =>
  api<{ id: string; status: 'deleted' }>(
    `/study/documents/${documentId}/pdf-annotations/${annotationId}`,
    { method: 'DELETE', headers: { 'if-match': String(expectedVersion) } },
  );

export type StudySessionMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  stars_charged: number;
  created_at: string;
};

export type StudySession = {
  notebook_source?: {
    id: string;
    version: number;
    current_version: number;
    changed: boolean;
    learner_text: string;
    truncated?: boolean;
    provenance: 'learner_authored';
  } | null;
  subject_tutor?: {
    status: 'published' | 'unpublished' | 'unsupported' | 'paused';
    label: string;
    binding: { key: string; revision: number; hash: string } | null;
  } | null;
  id: string;
  goal: 'understand' | 'homework' | 'revise' | 'assessment';
  status: 'active' | 'completed';
  highest_hint_level: number;
  help_policy?: { h4_allowed: boolean };
  summary: {
    attempt_recorded: boolean;
    highest_hint_level: number;
    tutor_turns: number;
    stars_used: number;
    next_step: string;
  } | null;
  review_due_at: string | null;
  completed_at: string | null;
  created_at: string;
  term: { id: string; term_number: number; label: string };
  subject: { subject_key: string; display_name: string };
  document: Omit<StudyDocumentDetail, 'source_revision'> & {
    tutor_source_ready: boolean;
    selected_page: {
      page_number: number;
      mime_type: 'image/png';
      size_bytes: number;
      provenance: 'client_pdfjs';
      read_url: string;
      read_url_expires_in: number;
    } | null;
    focused_region: {
      mime_type: 'image/png';
      size_bytes: number;
      provenance: 'client_crop';
      crop: { x: number; y: number; width: number; height: number };
      source_page_number: number | null;
      read_url: string;
      read_url_expires_in: number;
    } | null;
  };
  learning: {
    id: string;
    started_at: string;
    ended_at: string | null;
    stars_used: number;
    llm_calls: number;
  };
  attempt: { content: string; submitted_at: string; updated_at: string } | null;
  source_confirmation: {
    text: string;
    provenance: 'learner_confirmed';
    confirmed_at: string;
    /** S10: the stored text is the redacted text the learner accepted. */
    redacted?: boolean;
  } | null;
  generated_practice: StudyGeneratedPractice | null;
  messages: StudySessionMessage[];
  /**
   * The analysed question this session works on (study-capture-fixes-prd.md F-02). Optional:
   * until the session read returns it, the app reads the same ids from the session URL.
   */
  analysis_question?: { analysis_id: string; question_id: string } | null;
};

export type StudyGeneratedPractice = {
  id: string;
  provenance: 'ai_generated_from_private_source';
  verification: 'not_human_verified';
  source: { document_id: string; page_id: string | null; region_id: string | null };
  model: string;
  stars_charged: number;
  created_at: string;
  questions: Array<{
    id: string;
    stem: string;
    options: [string, string, string, string];
    attempt: {
      submitted: 'A' | 'B' | 'C' | 'D';
      is_correct: boolean;
      correct_answer: 'A' | 'B' | 'C' | 'D';
      explanation: string;
      source_evidence: string;
      created_at: string;
    } | null;
  }>;
};

/** A completed Study Session due for review. `kind` is absent on an older server. */
export type StudySessionReviewItem = {
  kind?: 'session';
  id: string;
  document_id: string;
  document_name: string;
  subject: { subject_key: string; display_name: string };
  completed_at: string;
  review_due_at: string;
  due: boolean;
  summary: StudySession['summary'];
};

/**
 * One row of `GET /study/review`: a completed session or an analysed material or question
 * (study-capture-fixes-prd.md G-03), sorted by `review_due_at`.
 */
export type StudyReviewItem = StudySessionReviewItem | StudyAnalysisReviewItem;

export function isAnalysisReviewItem(item: StudyReviewItem): item is StudyAnalysisReviewItem {
  return item.kind === 'analysis';
}

export type StudyPracticeYearLevel =
  | 'Year 7'
  | 'Year 8'
  | 'Year 9'
  | 'Year 10'
  | 'Year 11'
  | 'Year 12';

export type StudyPracticeQuestionSet = {
  context: {
    subject_key: string;
    year_level: StudyPracticeYearLevel;
    alignment: 'learner_selected';
  };
  questions: AcademyQuestion[];
  coverage_gap?: boolean;
  mistake_count?: number;
};

export type StudyPracticeAttemptResult = {
  is_correct: boolean;
  correct_answer: string | null;
  attempt_number: number;
  can_retry: boolean;
  mistake_resolved: boolean;
  transfer_validated: boolean;
  transfer_question: AcademyQuestion | null;
  review_completed: boolean;
  review_due_at: string | null;
  review_interval_days: number | null;
  micro_hint: string | null;
};

export type StudyTutorResult = {
  id: string;
  /** Stored SessionMessage ID. `id` above is the upstream completion ID. Old receipts may omit it. */
  assistant_message_id?: string;
  model: string;
  reply: string;
  stars_charged: number;
  balance_after: number;
};

export type StudySetupInput = {
  school_year: number;
  year_level: number;
  current_term: number;
  jurisdiction?: 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA' | null;
  school: {
    name: string | null;
    suburb: string | null;
    state: 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA' | null;
    acara_id: string | null;
  };
  subjects: Array<{ subject_key: string; display_name: string }>;
};

type SignedStudyUpload = {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expires_in: number;
  s3_key: string;
  file_name?: string;
  size_bytes: number;
};

export const STUDY_HOME_QUERY_KEY = ['study-home'] as const;
export const STUDY_DOCUMENTS_QUERY_KEY = ['study-documents'] as const;
export const STUDY_ASSESSMENTS_QUERY_KEY = ['study-assessments'] as const;
export const STUDY_TASKS_QUERY_KEY = ['study-tasks'] as const;
export const STUDY_SESSION_QUERY_KEY = (sessionId: string) => ['study-session', sessionId] as const;

export const getStudyHome = () => api<StudyHome>('/study/home');

/** Which subjects have interactive tools switched on (Super Admin; study-subject-tools-platform-prd.md §4.1). */
export type StudyToolAccess = {
  default_interactive_tools: boolean;
  subjects: Array<{ subject_key: string; interactive_tools: boolean }>;
};
/** Prefix for invalidation. Queries use `studyToolAccessQueryKey()`, which adds the learner id. */
export const STUDY_TOOL_ACCESS_QUERY_KEY = ['study-tool-access'] as const;
/** The answer is per learner, so a sibling on the same device never sees the previous answer. */
export const studyToolAccessQueryKey = (kidId: string | null = getKidSub()) =>
  [...STUDY_TOOL_ACCESS_QUERY_KEY, kidId] as const;
export const getStudyToolAccess = () => api<StudyToolAccess>('/study/tools/access');

/**
 * Whether a subject's interactive tools are on. Unknown (still loading, or the platform could not answer) counts as
 * on: the platform refuses writes for a switched-off subject anyway, and tools must not vanish on a network blip.
 */
export function studyToolsOn(access: StudyToolAccess | undefined, subjectKey: string): boolean {
  if (!access) return true;
  const row = access.subjects.find((subject) => subject.subject_key === subjectKey);
  return row ? row.interactive_tools : access.default_interactive_tools;
}
export const listStudyDocuments = () => api<StudyDocument[]>('/study/documents');
export const listStudyDocumentsPage = (filters: {
  subjectKey?: string;
  termId?: string;
  search: string;
  cursor?: string | null;
}) => {
  const params = new URLSearchParams();
  if (filters.subjectKey) params.set('subject_key', filters.subjectKey);
  if (filters.termId) params.set('term_id', filters.termId);
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.cursor) params.set('cursor', filters.cursor);
  return api<{ items: StudyDocument[]; next_cursor: string | null }>(
    `/study/documents/page?${params}`,
  );
};
export const STUDY_LINKS_QUERY_KEY = ['study-links'] as const;
export const listStudyLinks = () => api<StudyLink[]>('/study/links');
export const createStudyLink = (input: {
  url: string;
  title: string;
  subject_key?: string | null;
}) => api<StudyLink>('/study/links', { method: 'POST', body: input });
export const updateStudyLink = (
  linkId: string,
  input: { title?: string; subject_key?: string | null; status?: 'archived' },
) => api<StudyLink>(`/study/links/${linkId}`, { method: 'PATCH', body: input });
export const getStudyDocument = async (documentId: string) => {
  const document = await api<StudyDocumentDetail>(`/study/documents/${documentId}`);
  return { ...document, read_url: studySourceUrl(document.read_url) };
};
export const listStudyAssessments = () => api<StudyAssessment[]>('/study/assessments');
export const listStudyTasks = () => api<StudyTask[]>('/study/tasks');
/** One task by id. The list above holds only the 100 newest tasks; 404 means it is deleted. */
export const getStudyTask = (taskId: string) =>
  api<StudyTask>(`/study/tasks/${encodeURIComponent(taskId)}`);
/** Query key of one task; under STUDY_TASKS_QUERY_KEY, so task writes refresh it too. */
export const studyTaskQueryKey = (taskId: string) =>
  [...STUDY_TASKS_QUERY_KEY, 'one', taskId] as const;
export const createStudyTask = (input: {
  title: string;
  source_url?: string | null;
  subject_key?: string | null;
  due_on?: string | null;
}) => api<StudyTask>('/study/tasks', { method: 'POST', body: input });
export const updateStudyTask = (
  taskId: string,
  input: {
    status?: StudyTask['status'];
    title?: string;
    source_url?: string | null;
    subject_key?: string | null;
    due_on?: string | null;
  },
) => api<StudyTask>(`/study/tasks/${taskId}`, { method: 'PATCH', body: input });
export const getStudySession = (sessionId: string) =>
  api<StudySession>(`/study/sessions/${sessionId}`);
export const listStudyReview = () => api<StudyReviewItem[]>('/study/review');
export const getToolTutorPrice = (subject: 'chemistry' | 'physics') =>
  api<{ model: string; stars: number; source: string }>(`/study/tools/${subject}/tutor-price`);
export const getMathTutorPrice = () =>
  api<{ model: string; stars: number; source: string }>('/study/tools/mathematics/tutor-price');
/** The Study Tutor price: the `study_tutor` AI function, which follows chat until configured (SIR-OPS-02). */
export const getStudyTutorPrice = () =>
  api<{ model: string; stars: number; source: string }>('/study/tutor-price');

export const listStudyPracticeQuestions = (
  subjectKey: string,
  yearLevel: StudyPracticeYearLevel,
  limit = 5,
) =>
  api<StudyPracticeQuestionSet>(
    `/study/practice/questions?subject_key=${encodeURIComponent(subjectKey)}&year_level=${encodeURIComponent(yearLevel)}&limit=${limit}`,
  );

export const listStudyPracticeMistakes = (
  subjectKey: string,
  yearLevel: StudyPracticeYearLevel,
  limit = 5,
) =>
  api<StudyPracticeQuestionSet>(
    `/study/practice/mistakes?subject_key=${encodeURIComponent(subjectKey)}&year_level=${encodeURIComponent(yearLevel)}&limit=${limit}`,
  );

export const submitStudyPracticeAttempt = (input: {
  subject_key: string;
  year_level: StudyPracticeYearLevel;
  question_id: string;
  working_canvas_id?: string;
  submitted: string;
  time_ms?: number;
  attempt_kind: 'practice' | 'mistake_retry' | 'review' | 'transfer';
}) =>
  api<StudyPracticeAttemptResult>('/study/practice/attempts', {
    method: 'POST',
    body: input,
  });

export type ChemicalEquationPracticeResult = {
  id: string;
  catalog_version: string;
  activity_id: string;
  activity_version: number;
  activity_level: 'beginner' | 'intermediate' | 'advanced';
  attempt_count: number;
  highest_hint_level: number;
  attempt_kind: 'practice' | 'review';
  review_due_at: string;
  review_interval_days: number;
  evidence_kind: 'verified_practice_completion';
  mastery_claimed: false;
  completed_at: string;
};

export const submitChemicalEquationPractice = (input: {
  catalog_version: string;
  activity_id: string;
  activity_version: number;
  coefficients: number[];
  attempt_count: number;
  highest_hint_level: number;
  attempt_kind: 'practice' | 'review';
  idempotency_key: string;
}) =>
  api<ChemicalEquationPracticeResult>('/study/tools/chemical-equation/attempts', {
    method: 'POST',
    body: input,
  });

export type ChemicalEquationProgress = {
  catalog_version: string;
  completed_activities: Array<{ activity_id: string; activity_version: number }>;
  recent_attempts: ChemicalEquationPracticeResult[];
  mastery_claimed: false;
};

export type ChemicalEquationReviewItem = {
  id: string;
  tool_key: 'chemical-equation-lab';
  activity_id: string;
  activity_version: number;
  catalog_version: string;
  activity_level: 'beginner' | 'intermediate' | 'advanced';
  title: string;
  equation: string;
  completed_at: string;
  review_due_at: string;
  review_interval_days: number;
  due: boolean;
  previous_attempt_count: number;
  previous_highest_hint_level: number;
  mastery_claimed: false;
};

export const listChemicalEquationActivities = () =>
  api<{ catalog_version: string; activities: EquationPracticeChallenge[] }>(
    '/study/tools/chemical-equation/activities',
  );

export const listChemicalEquationProgress = () =>
  api<ChemicalEquationProgress>('/study/tools/chemical-equation/progress');

export const listChemicalEquationReview = () =>
  api<ChemicalEquationReviewItem[]>('/study/tools/chemical-equation/review');

/**
 * Opens a Study Session on a document. By default the server reopens the learner's open session
 * for the same document; `fresh` always starts a new one (one session per analysed question).
 */
export const startStudySession = (
  documentId: string,
  goal: StudySession['goal'] = 'understand',
  options: {
    fresh?: boolean;
    /** Links the session to an analysed question; the server continues its open session. */
    question?: { analysisId: string; questionId: string };
  } = {},
) =>
  api<StudySession>('/study/sessions', {
    method: 'POST',
    body: {
      document_id: documentId,
      goal,
      ...(options.fresh ? { fresh: true } : {}),
      ...(options.question
        ? { analysis_id: options.question.analysisId, question_id: options.question.questionId }
        : {}),
    },
  });

export const askStudyTutor = (
  sessionId: string,
  input: {
    question: string;
    hint_level: number;
    source_scope?: 'page' | 'document';
    document_action?: 'question' | 'guide';
    document_digest?: string;
    expected_stars?: number;
    idempotency_key: string;
  },
) =>
  api<StudyTutorResult>(`/study/sessions/${sessionId}/tutor`, {
    method: 'POST',
    body: input,
  });

export const generateStudyPractice = (
  sessionId: string,
  input: { expected_stars?: number; idempotency_key: string },
) =>
  api<StudyGeneratedPractice>(`/study/sessions/${sessionId}/practice/generate`, {
    method: 'POST',
    body: input,
  });

export const submitGeneratedStudyPracticeAttempt = (
  sessionId: string,
  input: {
    practice_id: string;
    question_id: string;
    submitted: 'A' | 'B' | 'C' | 'D';
  },
) =>
  api<StudyGeneratedPractice>(`/study/sessions/${sessionId}/practice/attempts`, {
    method: 'POST',
    body: input,
  });

export const saveStudyAttempt = (sessionId: string, content: string) =>
  api<StudySession>(`/study/sessions/${sessionId}/attempt`, {
    method: 'PUT',
    body: { content },
  });

/** S10 PII summary: types and counts only (SIR-TRU-02). */
export type StudyPiiFinding = { type: string; count: number };
export type StudySourceTextResult = StudySession & {
  pii?: { checked: boolean; findings: StudyPiiFinding[]; redacted: boolean };
};

/**
 * Confirms the learner's source text (SIR-QA-07). `acceptRedaction` re-sends the same text after a
 * 409 STUDY_SOURCE_PII_REVIEW_REQUIRED, so only the redacted text is stored.
 */
export const confirmStudySourceText = (sessionId: string, text: string, acceptRedaction = false) =>
  api<StudySourceTextResult>(`/study/sessions/${sessionId}/source-text`, {
    method: 'PUT',
    body: acceptRedaction ? { text, pii_redaction_accepted: true } : { text },
  });

export const finishStudySession = (sessionId: string) =>
  api<StudySession>(`/study/sessions/${sessionId}/finish`, { method: 'POST' });

/** A saved session-summary note (SIR-HYG-09). The server returns the existing note on repeat. */
export interface StudySessionNote {
  id: string;
  artifact_type: 'session_summary';
  study_session_id: string;
}

export const saveStudySessionToNotes = (sessionId: string) =>
  api<StudySessionNote>(`/study/knowledge/artifacts/from-session/${sessionId}`, { method: 'POST' });

export type StudyJurisdiction = 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA';

/** Official state term dates for a calendar year (SIR-HYG-09); dates are `YYYY-MM-DD` or null. */
export interface StudySchoolCalendar {
  school_year: number;
  jurisdiction: StudyJurisdiction;
  terms: Array<{
    term_number: number;
    label: string;
    starts_on: string | null;
    ends_on: string | null;
    source_note: string | null;
  }>;
}

export const getStudySchoolCalendar = (year: number, state: StudyJurisdiction) =>
  api<StudySchoolCalendar>(
    `/study/catalog/school-calendars?year=${encodeURIComponent(String(year))}&state=${encodeURIComponent(state)}`,
  );

export async function selectStudyPdfPage(
  sessionId: string,
  pageNumber: number,
  image: Blob,
): Promise<StudySession> {
  const input = { page_number: pageNumber, size_bytes: image.size };
  const signed = await api<SignedStudyUpload>(
    `/study/sessions/${sessionId}/source-page/upload-intent`,
    { method: 'POST', body: input },
  );
  const uploaded = await fetch(signed.url, {
    method: signed.method,
    headers: signed.headers,
    body: image,
  });
  if (!uploaded.ok) throw new Error('The selected PDF page did not upload. Please try again.');
  return api<StudySession>(`/study/sessions/${sessionId}/source-page/complete`, {
    method: 'POST',
    body: { ...input, s3_key: signed.s3_key },
  });
}

export async function focusStudySourceRegion(
  sessionId: string,
  crop: { x: number; y: number; width: number; height: number },
  image: Blob,
): Promise<StudySession> {
  const input = { size_bytes: image.size, crop };
  const signed = await api<SignedStudyUpload>(
    `/study/sessions/${sessionId}/source-region/upload-intent`,
    { method: 'POST', body: input },
  );
  const uploaded = await fetch(signed.url, {
    method: signed.method,
    headers: signed.headers,
    body: image,
  });
  if (!uploaded.ok) throw new Error('The focused question did not upload. Please try again.');
  return api<StudySession>(`/study/sessions/${sessionId}/source-region/complete`, {
    method: 'POST',
    body: { ...input, s3_key: signed.s3_key },
  });
}

export const createStudyAssessment = (input: CreateStudyAssessmentInput) =>
  api<StudyAssessment>('/study/assessments', { method: 'POST', body: input });

export const updateStudyAssessment = (assessmentId: string, input: UpdateStudyAssessmentInput) =>
  api<StudyAssessment>(`/study/assessments/${assessmentId}`, {
    method: 'PATCH',
    body: input,
  });

export const organiseStudyDocument = (
  documentId: string,
  subjectKey: string | null,
  termId?: string,
) =>
  api<StudyDocument>(`/study/documents/${documentId}`, {
    method: 'PATCH',
    body: { subject_key: subjectKey, ...(termId ? { term_id: termId } : {}) },
  });

/** Longest learner-chosen material name; the server refuses a longer one. */
export const STUDY_MATERIAL_NAME_MAX = 120;

/** Rename a material. `null` returns it to its original title. */
export const renameStudyDocument = (documentId: string, displayName: string | null) =>
  api<StudyDocument>(`/study/documents/${documentId}/name`, {
    method: 'PATCH',
    body: { display_name: displayName },
  });

export type StudySkillEvidenceSummary = {
  context: {
    subject_key: string;
    year_level: StudyPracticeYearLevel;
    provenance: 'learner_selected';
    curriculum_release: null;
  };
  algorithm_version: string;
  mastery_state: 'not_assessed';
  mastery_confidence: null;
  reason: string;
  sampled_evidence_count: number;
  truncated: boolean;
  skills: Array<{
    skill_key: string;
    attempts: number;
    independent_correct: number;
    transfer_correct: number;
    supported_attempts: number;
  }>;
};

export const listStudySkillEvidence = (subjectKey: string, yearLevel: StudyPracticeYearLevel) =>
  api<StudySkillEvidenceSummary>(
    `/study/evidence/skills?subject_key=${encodeURIComponent(subjectKey)}&year_level=${encodeURIComponent(yearLevel)}`,
  );

export const undoStudyAutoOrganisation = (documentId: string) =>
  api<StudyDocument>(`/study/documents/${documentId}/undo-auto-organisation`, {
    method: 'POST',
  });

export type StudyDocumentDeletion = {
  id: string;
  document_id: string;
  status: 'requested' | 'processing' | 'failed' | 'completed';
  requested_at: string;
  completed_at: string | null;
  object_count: number | null;
  error_code: string | null;
};

export const deleteStudyDocument = (documentId: string) =>
  api<StudyDocumentDeletion>(`/study/privacy/documents/${documentId}`, { method: 'DELETE' });

export const listStudyDocumentDeletions = () =>
  api<StudyDocumentDeletion[]>('/study/privacy/deletions');

export const setupStudyTerm = (input: StudySetupInput) =>
  api<StudyHome>('/study/me/setup', { method: 'PUT', body: input });

export async function uploadStudyDocument(file: File, subjectKey?: string): Promise<StudyDocument> {
  // A type the server refuses gets a clear learner message instead of a raw 400.
  assertSupportedStudyFile(file);
  const mimeType = studyUploadMime(file);
  if (isLocalStudyExperience())
    return api<StudyDocument>('/study/experience/documents', {
      method: 'POST',
      body: { file, subject_key: subjectKey },
    });
  const embeddedTextPromise =
    mimeType === 'application/pdf'
      ? import('./studyPdfText')
          .then(({ extractStudyPdfEmbeddedText }) => extractStudyPdfEmbeddedText(file))
          .catch(() => null)
      : Promise.resolve(null);
  const intentBody = {
    file_name: file.name,
    mime_type: mimeType,
    size_bytes: file.size,
  };
  const signed = await api<SignedStudyUpload>('/study/documents/upload-intent', {
    method: 'POST',
    body: intentBody,
  });
  const uploaded = await fetch(signed.url, {
    method: signed.method,
    headers: signed.headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error('The file upload did not finish. Please try again.');
  const completed = await api<StudyDocument>('/study/documents/complete', {
    method: 'POST',
    body: {
      ...intentBody,
      s3_key: signed.s3_key,
      subject_key: subjectKey || null,
    },
  });
  if (mimeType === 'application/pdf') {
    // Free server-side text indexing; opening Tutor retries a failed/unfinished read.
    void api(`/study/documents/${completed.id}/learning/prepare`, { method: 'POST' }).catch(
      () => undefined,
    );
  }
  const embeddedText = await embeddedTextPromise;
  if (!embeddedText) return completed;
  try {
    return await api<StudyDocument>(`/study/documents/${completed.id}/context-suggestion`, {
      method: 'POST',
      body: embeddedText,
    });
  } catch {
    // Upload success is authoritative. Embedded-text classification is a
    // privacy-bounded enhancement and must never turn a stored file into a
    // false upload failure.
    return completed;
  }
}

export type StudyTaskScope = {
  academic_year_id?: string;
  term_id?: string;
  subject_key?: string;
  status?: StudyTask['status'];
  include_unfiled?: boolean;
};
export const listStudyTaskPage = (scope: StudyTaskScope, cursor?: string) =>
  api<{ items: StudyTask[]; next_cursor: string | null }>(
    `/study/tasks/page?${new URLSearchParams(
      Object.entries({ ...scope, cursor })
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    )}`,
  );
